import { COMMUNITIES_KEY, DETAIL, LIST, MEMBERS_QUERY_KEY } from '../api/query';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useCurrentActiveMember, useCurrentMember } from '../redux/selector';
import { managementApi, memberApi } from '../api';
import { shouldUseManagementApi } from '../utils/MemberUtils';
import { GetPdfStatementRequest, Member, MemberStatus, PageMember, SortDirection } from '@quick/client';
import { useEffect } from 'react';
import { dedupe } from '../utils/ArrayUtils';

const DEFAULT_LIST_PAGE_SIZE = 10;

export const EMPTY_MEMBER_FILTER: MemberFilter = {
  status: {},
  page: 0,
  size: DEFAULT_LIST_PAGE_SIZE,
};

export interface MemberFilter {
  page: number;
  size: number;
  status: Partial<Record<MemberStatus, boolean>>;
  name?: string;
  sortBy?: string;
  sortDirection?: SortDirection;
}

const status = (memberFilter: MemberFilter) => {
  const status = Object.entries(memberFilter.status)
    .filter(([_, v]) => v)
    .map(([k, _]) => k as MemberStatus)
    .sort();
  return status.length > 0 ? status : undefined;
};

export const memberKeys = {
  all: (communityId: number) => [COMMUNITIES_KEY, communityId, MEMBERS_QUERY_KEY],
  lists: (communityId: number) => [...memberKeys.all(communityId), LIST],
  list: (communityId: number, memberFilter: MemberFilter = EMPTY_MEMBER_FILTER) => [
    ...memberKeys.lists(communityId),
    memberFilter,
  ],
  allList: (communityId: number) => [...memberKeys.lists(communityId), 'All'],
  details: (communityId: number) => [...memberKeys.all(communityId), DETAIL],
  detail: (communityId: number, memberId: number) => [...memberKeys.details(communityId), memberId],
};

export const useMembersQuery = (memberFilter: MemberFilter) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const member = useCurrentMember()!;
  return useQuery(
    memberKeys.list(member.communityId, memberFilter),
    () => {
      if (shouldUseManagementApi(member)) {
        return managementApi.listMembers({
          ...memberFilter,
          communityId: member.communityId,
          status: status(memberFilter),
        });
      } else {
        return memberApi.listMembers1({
          ...memberFilter,
          communityId: member.communityId,
        });
      }
    },
    {
      getNextPageParam: (lastPage) => (lastPage.number < lastPage.totalPages ? lastPage.number + 1 : undefined),
      refetchOnWindowFocus: true,
      keepPreviousData: true,
      enabled: !!member,
    }
  );
};

export const usePrefetchMembers = () => {
  const member = useCurrentActiveMember();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!member) {
      return;
    }
    const communityId = member.communityId;
    queryClient.prefetchQuery(memberKeys.list(communityId), () => {
      if (shouldUseManagementApi(member)) {
        return managementApi.listMembers({
          communityId,
        });
      } else {
        return memberApi.listMembers1({
          communityId,
        });
      }
    });
  }, [member, queryClient]);
};

export const useMemberQuery = (memberId: number) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentMember = useCurrentMember()!;
  return useQuery(memberKeys.detail(currentMember.communityId, memberId), () =>
    memberApi.getMember({
      memberId,
    })
  );
};

export const useAllActiveMembers = () => {
  const members = useAllMembersQuery();
  const allMembers = members.data ?? [];
  return allMembers.filter((it) => it.status === MemberStatus.Active);
};

export const useAllInactiveMembers = () => {
  const members = useAllMembersQuery();
  const allMembers = members.data ?? [];
  return allMembers.filter((it) => it.status !== MemberStatus.Active);
};

export const useAllMembersQuery = () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentMember = useCurrentMember()!;
  const queryClient = useQueryClient();
  const communityId = currentMember.communityId;
  return useQuery<Member[]>(
    memberKeys.allList(currentMember.communityId),
    async ({ pageParam }) => {
      let list: Member[] = [];
      let lastPage: PageMember;
      do {
        if (shouldUseManagementApi(currentMember)) {
          lastPage = await managementApi.listMembers({
            communityId,
            page: pageParam ?? 0,
          });
        } else {
          lastPage = await memberApi.listMembers1({
            communityId,
            page: pageParam ?? 0,
          });
        }
        pageParam = lastPage.number + 1;
        list = [...list, ...lastPage.content];
      } while (lastPage.number < lastPage.totalPages);
      return dedupe(list, (it) => it.memberId).sort((a, b) => a.name.localeCompare(b.name));
    },
    {
      staleTime: 5 * 60 * 1000,
      onSuccess(members) {
        for (const member of members) {
          queryClient.setQueryData(memberKeys.detail(communityId, member.memberId), (old) => member);
        }
      },
    }
  );
};

export const useStatementMutation = () => {
  const getPdfStatementMutation = useMutation(
    (request: GetPdfStatementRequest) => memberApi.getPdfStatement(request),
    {}
  );

  return {
    getPdfStatementMutation,
  };
};
